import { useEffect, useState } from 'react';
import { getMessages, sendMessage, markReplyRead, getUsers, GetMessagesOutputType, GetUsersOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Send, Edit, Inbox, Mail, MessageSquare, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow, parseISO } from 'date-fns';

type Reply = GetMessagesOutputType['replies'][0];
type Msg = GetMessagesOutputType['sent'][0];
type User = GetUsersOutputType['users'][0];

const timeAgo = (d?: string) => { try { return d ? formatDistanceToNow(parseISO(d), { addSuffix: true }) : ''; } catch { return ''; } };

export default function MessagingTab() {
  const [tab, setTab] = useState<'inbox' | 'sent'>('inbox');
  const [replies, setReplies] = useState<Reply[]>([]);
  const [sent, setSent] = useState<Msg[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState<Msg | null>(null);

  // Compose form
  const [channel, setChannel] = useState<'Email' | 'SMS' | 'Both'>('Email');
  const [recipientType, setRecipientType] = useState<'all' | 'role' | 'department' | 'individual'>('all');
  const [recipientValue, setRecipientValue] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const load = () => {
    Promise.all([getMessages({}), getUsers({})]).then(([msgs, usrs]) => {
      setReplies(msgs.replies); setSent(msgs.sent); setUsers(usrs.users);
    }).catch(() => toast.error('Failed to load messages')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleMarkRead = async (id: string) => {
    await markReplyRead({ id }).catch(() => {});
    setReplies(prev => prev.map(r => r.id === id ? { ...r, read: true } : r));
  };

  const handleSend = async () => {
    if (!body.trim()) { toast.error('Message body is required'); return; }
    if (!subject.trim() && channel !== 'SMS') { toast.error('Subject is required for email'); return; }
    setSending(true);
    try {
      await sendMessage({
        subject: subject || body.slice(0, 50),
        body,
        channel,
        recipientType,
        recipientValue: recipientType !== 'all' ? recipientValue : undefined,
      });
      toast.success('Message sent');
      setComposeOpen(false);
      setSubject(''); setBody(''); setRecipientValue(''); setRecipientType('all');
      load();
    } catch (e: any) { toast.error(e.message || 'Failed to send'); }
    finally { setSending(false); }
  };

  const recipientCount = () => {
    if (recipientType === 'all') return users.length;
    if (recipientType === 'role') return users.filter(u => u.role === recipientValue).length;
    if (recipientType === 'department') return users.filter(u => u.department === recipientValue).length;
    return 1;
  };

  const unread = replies.filter(r => !r.read).length;

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-foreground">Messaging Centre</h2>
        <Button onClick={() => setComposeOpen(true)}><Edit className="w-4 h-4 mr-1.5" /> Compose</Button>
      </div>

      <div className="flex gap-1 mb-4 border-b border-border">
        <button
          onClick={() => setTab('inbox')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 -mb-px ${tab === 'inbox' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          <Inbox className="w-3.5 h-3.5" /> Inbox
          {unread > 0 && <span className="bg-primary text-primary-foreground text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{unread}</span>}
        </button>
        <button
          onClick={() => setTab('sent')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === 'sent' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Sent ({sent.length})
        </button>
      </div>

      {tab === 'inbox' && (
        <div className="border border-border rounded-lg divide-y divide-border">
          {replies.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">No inbound replies yet</div>
          ) : replies.map(r => (
            <div
              key={r.id}
              onClick={() => handleMarkRead(r.id)}
              className={`px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-muted/30 transition-colors ${!r.read ? 'bg-primary/5' : ''}`}
            >
              {!r.read && <div className="w-2 h-2 rounded-full bg-primary shrink-0" />}
              {r.read && <div className="w-2 h-2 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center">
                  <p className={`text-sm ${!r.read ? 'font-semibold text-foreground' : 'text-foreground'}`}>{r.employeeName}</p>
                  <span className="text-xs text-muted-foreground">{timeAgo(r.receivedAt)}</span>
                </div>
                <p className="text-xs text-muted-foreground truncate">{r.replyBody}</p>
              </div>
              <span className="text-xs bg-muted px-2 py-0.5 rounded shrink-0">{r.channel}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'sent' && (
        <div className="border border-border rounded-lg divide-y divide-border">
          {sent.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">No messages sent yet</div>
          ) : sent.map(m => (
            <div key={m.id} onClick={() => setSelectedMsg(m)} className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-muted/30">
              <div className={`p-1.5 rounded bg-muted ${m.channel === 'Email' ? 'text-primary' : 'text-muted-foreground'}`}>
                {m.channel === 'SMS' ? <MessageSquare className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center">
                  <p className="text-sm font-medium text-foreground truncate">{m.subject || '(no subject)'}</p>
                  <span className="text-xs text-muted-foreground shrink-0 ml-2">{timeAgo(m.sentAt)}</span>
                </div>
                <p className="text-xs text-muted-foreground truncate">{m.body}</p>
              </div>
              <span className="text-xs bg-muted px-2 py-0.5 rounded shrink-0">{m.channel}</span>
            </div>
          ))}
        </div>
      )}

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Compose Message</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Send via</label>
                <Select value={channel} onValueChange={v => setChannel(v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="SMS">SMS</SelectItem>
                    <SelectItem value="Both">Email & SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Recipients</label>
                <Select value={recipientType} onValueChange={v => { setRecipientType(v as any); setRecipientValue(''); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    <SelectItem value="role">By Role</SelectItem>
                    <SelectItem value="department">By Department</SelectItem>
                    <SelectItem value="individual">Individual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {recipientType === 'role' && (
              <Select value={recipientValue} onValueChange={setRecipientValue}>
                <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Trainee">Trainee</SelectItem>
                  <SelectItem value="Manager">Manager</SelectItem>
                  <SelectItem value="Admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            )}
            {recipientType === 'department' && (
              <Select value={recipientValue} onValueChange={setRecipientValue}>
                <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Front of House">Front of House</SelectItem>
                  <SelectItem value="Back of House">Back of House</SelectItem>
                  <SelectItem value="Kitchen">Kitchen</SelectItem>
                  <SelectItem value="Management">Management</SelectItem>
                </SelectContent>
              </Select>
            )}
            {recipientType === 'individual' && (
              <Select value={recipientValue} onValueChange={setRecipientValue}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.firstName} {u.lastName} — {u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            {recipientType !== 'individual' && (
              <p className="text-xs text-muted-foreground">~{recipientCount()} recipients</p>
            )}

            {channel !== 'SMS' && (
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Subject</label>
                <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Message subject" />
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Message</label>
              <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Type your message..." className="min-h-28" />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSend} disabled={sending} className="flex-1">
                {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-1.5" /> Send</>}
              </Button>
              <Button variant="outline" onClick={() => setComposeOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sent message detail */}
      <Dialog open={!!selectedMsg} onOpenChange={open => !open && setSelectedMsg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{selectedMsg?.subject || '(no subject)'}</DialogTitle></DialogHeader>
          {selectedMsg && (
            <div className="space-y-3 pt-2">
              <div className="flex gap-4 text-xs text-muted-foreground">
                <span>Channel: <strong>{selectedMsg.channel}</strong></span>
                <span>Sent by: <strong>{selectedMsg.sentByName}</strong></span>
                <span>{timeAgo(selectedMsg.sentAt)}</span>
              </div>
              <div className="border border-border rounded-md p-3 text-sm text-foreground whitespace-pre-wrap">{selectedMsg.body}</div>
              <div className="text-xs text-muted-foreground">
                Recipients: {(() => { try { return JSON.parse(selectedMsg.recipients).length + ' people'; } catch { return selectedMsg.recipients; } })()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
